import { z } from 'zod';
import { healthStatusSchema, moneySchema } from './common.js';
import { permissionKeySchema } from './permissions.js';
export const budgetEnvelopePeriodSchema = z
    .object({
    startDate: z.iso.date().nullable(),
    endDate: z.iso.date().nullable(),
    noEndDate: z.boolean(),
})
    .superRefine((value, context) => {
    if (value.noEndDate && value.endDate !== null) {
        context.addIssue({
            code: 'custom',
            path: ['endDate'],
            message: 'Open-ended envelopes cannot have an end date.',
        });
    }
    if ((value.noEndDate || value.endDate !== null) && value.startDate === null) {
        context.addIssue({
            code: 'custom',
            path: ['startDate'],
            message: 'A start date is required for a configured envelope period.',
        });
    }
    if (value.startDate && value.endDate && value.startDate > value.endDate) {
        context.addIssue({
            code: 'custom',
            path: ['endDate'],
            message: 'Envelope end date must follow its start date.',
        });
    }
});
export const budgetTimelineSchema = z.object({
    startDate: z.iso.date().nullable(),
    endDate: z.iso.date().nullable(),
    openEnded: z.boolean(),
    configuredEnvelopeCount: z.number().int().nonnegative(),
    incompleteEnvelopeCount: z.number().int().nonnegative(),
});
const budgetEnvelopeFields = {
    name: z.string().trim().min(1).max(240),
    categoryId: z.uuid(),
    budgetMinutes: z.number().int().nonnegative().max(10_000_000),
    budgetAmountMinor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    currency: z.enum(['CAD', 'USD']),
    fundingType: z.enum(['legacy', 'edt', 'ddc']).optional(),
    parentId: z.uuid().nullable().optional(),
};
export const budgetEnvelopeInputSchema = z
    .object({ projectId: z.uuid(), ...budgetEnvelopeFields })
    .and(budgetEnvelopePeriodSchema);
export const budgetEnvelopeUpdateInputSchema = z
    .object({ ...budgetEnvelopeFields, updatedAt: z.iso.datetime().optional() })
    .and(budgetEnvelopePeriodSchema);
export const budgetEnvelopeSummarySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    category: z.string(),
    fundingType: z.enum(['legacy', 'edt', 'ddc']).optional(),
    parentId: z.uuid().nullable().optional(),
    budgetMinutes: z.number().int().nonnegative(),
    budget: moneySchema,
    startDate: z.iso.date().nullable(),
    endDate: z.iso.date().nullable(),
    noEndDate: z.boolean(),
    archivedAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
});
export const soldAverageRateSchema = z.object({
    rate: moneySchema.nullable(),
    source: z.enum(['calculated', 'unavailable']),
    reason: z.string().nullable(),
});
export const budgetValuationSchema = z.object({
    state: z.enum(['complete', 'partial', 'unavailable']),
    unratedMinutes: z.number().int().nonnegative(),
});
export const budgetEnvelopeRestoreInputSchema = z.object({
    updatedAt: z.iso.datetime(),
});
export const budgetSummarySchema = z.object({
    clientId: z.uuid(),
    clientName: z.string(),
    projectId: z.uuid(),
    projectName: z.string(),
    budget: moneySchema,
    consumed: moneySchema,
    forecast: moneySchema,
    remaining: moneySchema,
    burnRatio: z.number().nullable(),
    health: healthStatusSchema,
    contractSigned: z.boolean(),
    soldAverageRate: soldAverageRateSchema,
    valuation: budgetValuationSchema,
    timeline: budgetTimelineSchema,
    envelopes: z.array(budgetEnvelopeSummarySchema),
});
export const budgetReadSummarySchema = budgetSummarySchema.extend({
    healthReason: z.string().nullable(),
    permissions: z.array(permissionKeySchema),
    consumedMinutes: z.number().int().nonnegative().optional(),
    remainingMinutes: z.number().int().nonnegative().optional(),
});
export const budgetPhaseInputSchema = z
    .object({
    name: z.string().trim().min(1).max(160),
    position: z.number().int().nonnegative(),
    budgetAmountMinor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).nullable(),
})
    .strict();
export const budgetPhaseUpdateSchema = budgetPhaseInputSchema.extend({
    updatedAt: z.iso.datetime(),
});
export const budgetVersionSchema = z.object({ updatedAt: z.iso.datetime() }).strict();
export const budgetPhaseMappingInputSchema = z
    .object({ phaseId: z.uuid().nullable(), updatedAt: z.iso.datetime().nullable() })
    .strict();
export const budgetDashboardResponseSchema = z.object({
    data: z.object({
        summary: budgetReadSummarySchema,
        referenceAmountMinor: z.number().int(),
        referenceReady: z.boolean(),
        unclassifiedAmountMinor: z.number().int(),
        averageRateMinor: z.number().int().nullable(),
        financeableMinutes: z.number().int().nullable(),
        ddcs: z.array(z.object({ id: z.uuid(), name: z.string(), amountMinor: z.number().int() })),
        months: z.array(z.object({
            month: z.string(),
            minutes: z.number().int(),
            consumedMinor: z.number().int(),
            cumulativeMinutes: z.number().int(),
            cumulativeConsumedMinor: z.number().int(),
        })),
        series: z.array(z.object({
            date: z.iso.date(),
            consumedMinor: z.number().int().nullable(),
            linearConsumedMinor: z.number().int().nullable(),
            financeableMinutes: z.number().int().nullable(),
            linearFinanceableMinutes: z.number().int().nullable(),
        })),
        phases: z.array(z.object({
            id: z.uuid().nullable(),
            name: z.string(),
            position: z.number().int(),
            budgetAmountMinor: z.number().int().nullable(),
            consumedMinor: z.number().int(),
            remainingMinutes: z.number().int(),
            remainingMinor: z.number().int(),
            unratedMinutes: z.number().int(),
            updatedAt: z.iso.datetime().nullable(),
        })),
        mappings: z.array(z.object({ workItemId: z.uuid(), phaseId: z.uuid(), updatedAt: z.iso.datetime() })),
        epics: z.array(z.object({ id: z.string(), name: z.string(), minutes: z.number().int() })),
        workItems: z.array(z.object({ id: z.uuid(), key: z.string(), name: z.string() })),
        canManage: z.boolean(),
    }),
});
//# sourceMappingURL=budgets.js.map